<template>
    <div id="role-edit">
        <div class="card shadow-sm">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                        <SupsisButton icon="arrow_back" class="mr-3" @click="handleCancel" />
                        <h4 class="mb-0">{{ roleId === "new" ? $t("message.newRoleCreate") : $t("message.editRole") }}</h4>
                    </div>
                    <div class="card-actions d-flex">
                        <SupsisButton ghost class="mr-2" @click="handleCancel">
                            <span class="material-icons mr-1">close</span>
                            {{ $t("message.cancelText") }}
                        </SupsisButton>
                        <SupsisButton primary @click="handleSave" :disabled="loading">
                            <template v-if="!loading">
                                <span class="material-icons mr-1">{{ roleId === "new" ? "add_circle" : "save" }}</span>
                                {{ roleId === "new" ? $t("message.create") : $t("message.save") }}
                            </template>
                            <template v-else>
                                <span class="material-icons spinner mr-1">autorenew</span>
                                {{ roleId === "new" ? $t("message.creatingRole") : $t("message.savingRole") }}
                            </template>
                        </SupsisButton>
                    </div>
                </div>
            </div>

            <div class="card-body">
                <div v-if="$validator.errors.items.length" class="alert alert-danger">
                    <div v-for="err of $validator.errors.items" :key="err.id">
                        <span>{{ err.msg }}</span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-12 mb-4">
                        <div class="form-group">
                            <label>{{ $t("message.roleName") }}</label>
                            <input
                                v-model="role.name"
                                v-validate="'required'"
                                :name="$t('message.roleName')"
                                :placeholder="$t('message.roleName')"
                                class="form-control"
                            />
                        </div>
                    </div>
                    <div class="col-md-12 mb-4">
                        <div class="permission-section">
                            <div class="section-header mb-3 d-flex justify-content-between align-items-center">
                                <h4>{{ $t("message.rolePermissions") }}</h4>
                                <ModuleToggle
                                    :is-active="isAllPermissionsSelected"
                                    :is-partial="isPartiallySelected"
                                    :title="$t('message.toggleAllPermissions')"
                                    @toggle="toggleAllPermissions"
                                />
                            </div>

                            <div
                                class="permission-card mb-3"
                                v-for="moduleGroup in groupedPermissions"
                                :key="`module-${moduleGroup.key}`"
                            >
                                <module-header
                                    :module-name="moduleGroup.module"
                                    :module-icon="getModuleIcon(moduleGroup.key)"
                                    :is-module-fully-selected="isModuleFullySelected(moduleGroup)"
                                    :is-module-partially-selected="isModulePartiallySelected(moduleGroup)"
                                    :is-expanded="expandedModules[moduleGroup.key]"
                                    @toggle-accordion="toggleModuleAccordion(moduleGroup.key)"
                                    @toggle-all="toggleAllModulePermissions(moduleGroup)"
                                />
                                <div v-show="expandedModules[moduleGroup.key]" role="tabpanel">
                                    <div class="p-0">
                                        <div class="permissions-list">
                                            <template v-for="item in moduleGroup.items">
                                                <template v-if="item.key === 'basic-actions'">
                                                    <sub-entity-group
                                                        :key="'entity-' + item.key"
                                                        :subEntity="item"
                                                        :selectedPermissions="role.permissions"
                                                        :showAsCategory="moduleGroup.items.length > 1"
                                                        @toggle="togglePermissionWithDependency"
                                                        @toggle-all="toggleAllSubEntityPermissions"
                                                    />
                                                </template>
                                                <template v-else>
                                                    <sub-entity-group
                                                        :key="'entity-' + item.key"
                                                        :subEntity="item"
                                                        :selectedPermissions="role.permissions"
                                                        :showAsCategory="true"
                                                        @toggle="togglePermissionWithDependency"
                                                        @toggle-all="toggleAllSubEntityPermissions"
                                                    />
                                                </template>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { log2 } from "../../../../../../utils/log2";
import { Permissions, groupPermissions } from "./utils/Permissions";
import PermissionItem from "./components/PermissionItem.vue";
import SubEntityGroup from "./components/SubEntityGroup.vue";
import ModuleHeader from "./components/ModuleHeader.vue";
import { getModuleIcon, getSubEntityIcon, getActionIcon } from "./components/IconUtils";
import SupsisButton from "../../../components/SupsisButton.vue";
import ModuleToggle from "./components/ModuleToggle.vue";

const log = log2("RoleEdit");

export default {
    name: "RoleEdit",
    components: {
        SupsisButton,
        PermissionItem,
        SubEntityGroup,
        ModuleHeader,
        ModuleToggle,
    },
    props: {
        roleId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            role: {
                _id: null,
                name: "",
                permissions: [],
                isRoot: false,
            },
            expandedModules: {},
            permissionModels: Permissions,
        };
    },
    computed: {
        ...mapState({
            roles: (state) => state.role.roles.data,
        }),
        groupedPermissions() {
            return groupPermissions(this.permissionModels);
        },
        isAllPermissionsSelected() {
            return this.permissionModels.every(p => this.hasPermissionValue(p._id));
        },
        isPartiallySelected() {
            const selectedCount = this.permissionModels.filter(p => this.hasPermissionValue(p._id)).length;
            return selectedCount > 0 && selectedCount < this.permissionModels.length;
        },
    },
    methods: {
        ...mapActions({
            updateRole: "role/updateRole",
            createRole: "role/createRole",
        }),
        toggleModuleAccordion(moduleName) {
            this.$set(this.expandedModules, moduleName, !this.expandedModules[moduleName]);
        },
        hasPermissionValue(permissionId) {
            if (!permissionId) return false;
            return this.role.permissions.includes(permissionId);
        },
        toggleAllModulePermissions(moduleGroup) {
            const allSelected = this.isModuleFullySelected(moduleGroup);
            const allPermissions = moduleGroup.items.reduce((acc, item) => {
                return [...acc, ...item.actions.map((p) => p._id)];
            }, []);

            if (allSelected) {
                this.role.permissions = this.role.permissions.filter((p) => !allPermissions.includes(p));
            } else {
                this.role.permissions = [
                    ...this.role.permissions.filter((p) => !allPermissions.includes(p)),
                    ...allPermissions,
                ];
            }
        },
        toggleAllSubEntityPermissions(permissions) {
            const allSelected = this.isSubEntityFullySelected(permissions);

            if (allSelected) {
                this.role.permissions = this.role.permissions.filter((p) => !permissions.includes(p));
            } else {
                this.role.permissions = [
                    ...this.role.permissions.filter((p) => !permissions.includes(p)),
                    ...permissions,
                ];
            }
        },
        togglePermissionWithDependency(permission) {
            const { _id, name, isViewPermission } = permission;
            console.log("togglePermissionWithDependency", permission, this.hasPermissionValue(_id));
            let permissions = [...this.role.permissions, _id];
            if (this.hasPermissionValue(_id)) {
                let removingPermissions = [_id];
                if (isViewPermission) {
                    const moduleGroup = this.groupedPermissions.find((g) => g.key === name.split(".")[0]);
                    if (moduleGroup) {
                        const allPermissions = moduleGroup.items.reduce((acc, item) => {
                            return [...acc, ...item.actions.map((p) => p._id)];
                        }, []);
                        removingPermissions = allPermissions;
                    }
                }
                permissions = permissions.filter((p) => !removingPermissions.includes(p));
            }
            this.role = {
                ...this.role,
                permissions,
            };
        },
        toggleAllPermissions() {
            if (this.isAllPermissionsSelected) {
                this.role.permissions = [];
            } else {
                this.role.permissions = this.permissionModels.map((p) => p._id);
            }
        },
        getModuleIcon,
        getSubEntityIcon,
        getActionIcon,
        isModuleFullySelected(moduleGroup) {
            const allPermissions = moduleGroup.items.reduce(
                (acc, item) => [...acc, ...item.actions.map((p) => p._id)],
                []
            );
            return allPermissions.every((p) => this.hasPermissionValue(p));
        },
        isModulePartiallySelected(moduleGroup) {
            const allPermissions = moduleGroup.items.reduce(
                (acc, item) => [...acc, ...item.actions.map((p) => p._id)],
                []
            );
            const selectedCount = allPermissions.filter((p) => this.hasPermissionValue(p)).length;
            return selectedCount > 0 && selectedCount < allPermissions.length;
        },
        isSubEntityFullySelected(permissions) {
            return permissions.every((p) => this.hasPermissionValue(p));
        },
        handleCancel() {
            this.$emit("cancelled");
        },
        async handleSave() {
            try {
                const isValid = await this.$validator.validateAll();
                if (!isValid) return;

                this.loading = true;

                const allPermissions = this.groupedPermissions.reduce((acc, moduleGroup) => {
                    const modulePermissions = moduleGroup.items.reduce(
                        (acc2, item) => [...acc2, ...item.actions.map((p) => p._id)],
                        []
                    );
                    return [...acc, ...modulePermissions];
                }, []);

                const roleCopy = { ...this.role };
                const isRoot = roleCopy.permissions.length === allPermissions.length;
                roleCopy.isRoot = isRoot;
                if(isRoot) {
                    roleCopy.permissions = [];
                }


                if (roleCopy._id) {
                    await this.updateRole(roleCopy);
                } else {
                    await this.createRole(roleCopy);
                }

                this.$toasted.show(this.$t("message.roleSaved"), {
                    duration: 3000,
                    position: "bottom-center",
                });

                this.$emit("saved");
            } catch (error) {
                log.error(error);
                this.$toasted.error("Rol kaydedilirken bir hata oluştu", {
                    duration: 3000,
                    position: "bottom-center",
                });
            } finally {
                this.loading = false;
            }
        },
         loadRole() {
            if (this.roleId === "new") {
                this.role = {
                    _id: null,
                    name: "",
                    permissions: [],
                    isRoot: false,
                };
                return;
            }

            try {
                const role = this.roles.find((r) => r._id === this.roleId);
                if (role) {
                    const allPermissions = this.groupedPermissions.reduce((acc, moduleGroup) => {
                        const modulePermissions = moduleGroup.items.reduce(
                            (acc2, item) => [...acc2, ...item.actions.map((p) => p._id)],
                            []
                        );
                        return [...acc, ...modulePermissions];
                    }, []);

                    this.role = {
                        ...role,
                        permissions: role.isRoot
                            ? allPermissions
                            : role.permissions.map((p) => (typeof p === "object" ? p._id : p)),
                    };
                }
            } catch (error) {
                console.error(error);
                this.$toasted.error("Rol yüklenirken bir hata oluştu", {
                    duration: 3000,
                    position: "bottom-center",
                });
            }
        },
    },
    async created() {
        this.groupedPermissions.forEach((moduleGroup) => {
            this.$set(this.expandedModules, moduleGroup.module, false);
        });
    },
    watch: {
        roleId: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.loadRole();
                }
            },
        },
        roles: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.loadRole();
                }
            },
        },
    },
};
</script>

<style lang="scss" scoped>
#role-edit {
    .form-control {
        border-radius: 4px;
        border: 1px solid #e0e0e0;
        box-shadow: none;
        transition: all 0.2s;

        &:focus {
            border-color: #4d7cfe;
            box-shadow: 0 0 0 0.2rem rgba(77, 124, 254, 0.15);
        }
    }

    .card {
        border-radius: 6px;
        border: none;

        .card-header {
            background-color: #fff;
            border-bottom: 1px solid #f0f0f0;
            padding: 1rem 1.25rem;

            h4 {
                font-weight: 600;
                color: #333;
                font-size: 1.1rem;
            }

            .btn-icon {
                width: 36px;
                height: 36px;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 8px;
                background: transparent;
                color: #666;
                border: none;
                transition: all 0.2s ease;

                &:hover {
                    background-color: #f5f5f5;
                    color: #333;
                }

                .material-icons {
                    font-size: 20px;
                }
            }

            .btn {
                height: 38px;
                padding: 0 1rem;
                font-weight: 500;
                display: flex;
                align-items: center;
                border-radius: 8px;
                transition: all 0.2s ease;

                .material-icons {
                    font-size: 18px;
                }

                &.btn-soft-secondary {
                    background-color: #f8f9fa;
                    border: 1px solid #f0f0f0;
                    color: #666;

                    &:hover {
                        background-color: #f1f3f5;
                        border-color: #e9ecef;
                        color: #333;
                    }
                }

                &.btn-primary {
                    background-color: #4d7cfe;
                    border: none;
                    color: #fff;

                    &:hover:not(:disabled) {
                        background-color: #3d6ce9;
                    }

                    &:disabled {
                        opacity: 0.7;
                        cursor: not-allowed;
                    }

                    .spinner {
                        animation: spin 1s linear infinite;
                    }
                }
            }
        }

        @keyframes spin {
            from {
                transform: rotate(0deg);
            }

            to {
                transform: rotate(360deg);
            }
        }
    }

    .permission-card {
        border: none;
        border-radius: 6px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        margin-bottom: 1rem;
        transition: all 0.2s ease;

        &:hover {
            box-shadow: 0 3px 8px rgba(0, 0, 0, 0.07);
        }
    }

    .module-header {
        .btn-light {
            background-color: #fff;
            border: none;
            padding: 0.8rem 1.2rem;
            border-radius: 0;
            border-bottom: 1px solid #f5f5f5;
            width: 100%;
            text-align: left;

            &:hover,
            &:focus {
                background-color: #f9f9f9;
            }

            &:active {
                background-color: #f5f5f5;
            }
        }

        .module-title {
            display: flex;
            align-items: center;
            font-weight: 600;
            color: #333;
            font-size: 0.95rem;

            span {
                color: #4d7cfe;
                width: 22px;
                font-size: 1rem;
            }
        }

        .module-toggle-group {
            display: flex;
            margin-right: 12px;

            & > div {
                width: 28px;
                height: 28px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                margin-left: 6px;
                border-radius: 4px;
                transition: all 0.2s;

                &:hover {
                    background-color: #f5f5f5;
                }

                span {
                    font-size: 16px;
                }
            }

            .toggle-all {
                color: #999;

                &:hover {
                    color: #666;
                }

                &.active {
                    color: #4caf50;

                    &:hover {
                        color: #388e3c;
                    }
                }
            }
        }

        .module-collapse-icon {
            color: #999;
            font-size: 0.9rem;
        }
    }

    .section-header {
        padding-bottom: 0.75rem;
        border-bottom: 1px solid #f0f0f0;
        margin-bottom: 1.5rem;

        h4 {
            font-weight: 600;
            color: #333;
            font-size: 1.1rem;
            margin: 0;
        }
    }

    .permissions-list {
        padding: 0;
        background-color: #fff;

        > .permission-item {
            border-bottom: 1px solid #f5f5f5;
            padding: 0.6rem 1.25rem;
            margin-bottom: 0.1rem;
        }
    }
}
</style>
